import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { SquarePenIcon, Trash2Icon } from 'lucide-react';
import { useState } from 'react';
import { Changelog } from '../../types/Changelog/Changelog.type';
import { getChangelogStatusStyle } from '../../utils/changelogStatusStyle';
import { formatReleaseDate } from '../../utils/formatReleaseDate';

interface ChangelogRowProps {
  changelog: Changelog;
  onEdit: (changelogId: number) => void;
  onDelete: (changelogId: number) => void;
  isDeleting: boolean;
}

function ChangelogRow({ changelog, onEdit, onDelete, isDeleting }: ChangelogRowProps) {
  const statusStyle = getChangelogStatusStyle(changelog.status_id);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleDeleteConfirm = () => {
    onDelete(changelog.id);
    setDeleteDialogOpen(false);
  };

  return (
    <TableRow hover>
      <TableCell width={340}>
        <Typography variant='body2'>{changelog.title}</Typography>
      </TableCell>
      <TableCell width={160}>
        <Typography variant='body2'>
          {changelog.release_date ? formatReleaseDate(changelog.release_date) : '—'}
        </Typography>
      </TableCell>
      <TableCell width={120}>
        <Box
          component='span'
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            paddingX: 1,
            paddingY: '2px',
            borderRadius: '6px',
            backgroundColor: statusStyle.backgroundColor,
            color: statusStyle.color,
            fontSize: '12px',
            fontWeight: 600,
          }}
        >
          {statusStyle.name}
        </Box>
      </TableCell>
      <TableCell width={120}>
        <Typography variant='body2'>{changelog.items.length}</Typography>
      </TableCell>
      <TableCell align='right' width={120}>
        <Stack direction='row' spacing={1}>
          <IconButton aria-label='Edit changelog' onClick={() => onEdit(changelog.id)}>
            <SquarePenIcon size={20} />
          </IconButton>
          <IconButton aria-label='Delete changelog' onClick={() => setDeleteDialogOpen(true)} disabled={isDeleting}>
            <Trash2Icon size={20} />
          </IconButton>
        </Stack>
      </TableCell>
      <Dialog
        maxWidth='xs'
        fullWidth
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby='delete-changelog-dialog-title'
        aria-describedby='delete-changelog-dialog-description'
      >
        <DialogTitle sx={{ fontSize: '16px' }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '14px' }}>
            Are you sure you want to delete selected changelog entry? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color='error' variant='contained' disableElevation>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </TableRow>
  );
}

export default ChangelogRow;
